import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { MessagesStore } from '../stores';
import { IAPanel } from '../../panels/Panel';
import DateTimeService from '../../../services/DateTimeService';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import { IMessage } from '../../../interfaces/IMessage';
import MessageService from '../../../services/MessageService';
import { FilterService } from '../Filter.service';
import { IMessageResult } from '../../../interfaces/IMessageResult';
import { IAListView } from '../../listView/ListView';
import { IAButton } from '../../button/Button';
import InstanceService from '../../../services/InstanceService';
import { IInstance } from '../../../interfaces/IInstance';
import { IListElement } from '../../../interfaces/IPlay';
import { IAPopup } from '../../popup/Popup';
import { IHttpStatus } from '../../../interfaces/IHttpStatus';
import { ISkeletonElement } from '../../../interfaces/ISkeletonElement';

export interface ISortPinnedPanelProps {
  messagesStore?: MessagesStore;
  top?: number;
  getMessages: () => void;
}

export interface ISortPinnedPanelState {
  isLoading?: boolean;
  pinnedMessages?: IMessage[];
  expiredPinnedMessages?: IMessage[];
  hasError?: boolean;
}


@inject('messagesStore')
@observer
export class SortPinnedPanel extends React.Component<ISortPinnedPanelProps, ISortPinnedPanelState> {
  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private panelId: string;

  constructor(props: ISortPinnedPanelProps) {
    super(props);
    this.state = {
      isLoading: false,
      pinnedMessages: undefined,
      hasError: false
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    this.panelId = Helper.getRandomStringKey();
  }

  componentDidUpdate(): void {
    const hasNoData = !this.state.pinnedMessages && !this.state.isLoading;
    const shouldRefresh = this.props.messagesStore.refreshSortPinnedPanel;
    if (this.props.messagesStore.showSortPinnedPanel && (hasNoData || shouldRefresh) && !this.state.hasError) {
      this.getPinnedMessages();
    }
  }

  private getPinnedMessages() {
    this.setState({ isLoading: true, hasError: false }, () => {
      this.props.messagesStore.refreshSortPinnedPanel = false;
      const env = this.props.messagesStore.environment ?? "Production";
      const hasTargeting = this.props.messagesStore.isAdminPage ? false : this.props.messagesStore.isTargetingAvailable;
      MessageService.getPinnedMessages(
        env,
        this.props.messagesStore.token,
        this.props.messagesStore.tenant,
        this.props.messagesStore.instance.instanceName,
        hasTargeting,
        FilterService.get(this.props.messagesStore.isAdminPage, undefined, undefined, undefined, undefined, undefined, this.props.messagesStore.component),
        this.props.messagesStore.filterData.isFiltered,
        this.props.messagesStore.searchText
      ).then((pinnedMessagesResult: IMessageResult) => {
        if (pinnedMessagesResult.httpStatus.statusCode === 200) {
          if (this.props.messagesStore.instance?.settings?.pinSortOrder && pinnedMessagesResult.messages?.length > 0) {
            pinnedMessagesResult.messages = this.props.messagesStore.sortMessageBySortOrder(pinnedMessagesResult.messages, this.props.messagesStore.instance.settings.pinSortOrder);
          }
          this.setState({
            isLoading: false,
            pinnedMessages: pinnedMessagesResult?.messages.filter((message) => message.pinnedEndDate > new Date()),
            expiredPinnedMessages: pinnedMessagesResult?.messages.filter((message) => message.pinnedEndDate < new Date()),
            hasError: false
          });
        } else {
          this.setState({ hasError: true, isLoading: false });
        }
      });
    });
  }

  public render(): JSX.Element {
    let date: string;
    if (this.props.messagesStore.messageToOpen != undefined && this.props.messagesStore.messageToOpen.startDate != undefined) {
      date = this.dateTimeService.ConvertToDDMMYYYY(this.props.messagesStore.messageToOpen.startDate) === this.dateTimeService.ConvertToDDMMYYYY(new Date()) ? new LocalizationService().strings.Messages_ViewPanel_Today + " " + this.dateTimeService.ConvertToHHMM(this.props.messagesStore.messageToOpen.startDate) : this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.messagesStore.messageToOpen.startDate);
    }
    let top = 0;
    if (Helper.getDisplayEnvironment() === "Sharepoint" || Helper.getDisplayEnvironment() === "SharepointClassic") {
      top = 48;
    }
    if (this.props.messagesStore.isMobileApp) {
      top = window.platform === "android" ? 50 : Helper.isIPhoneXType() ? 90 : 70;
    }
    const pinnedMessagesList: IListElement[] = [];
    if (this.state.pinnedMessages?.length > 0) {
      this.state.pinnedMessages.forEach((message, index) => {
        pinnedMessagesList.push(
          {
            id: `${message.id}`,
            object: message,
            columns: [
              {
                header: "",
                type: "customElement",
                element: [
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    height: "100%"
                  }}>
                    <div style={{
                      marginRight: 10
                    }}>{index + 1}.</div>
                    <div>{message.title}</div>
                  </div>
                ]
              }
            ],
            buttons: [{
              action: "unpin",
              iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Pin Off/SVG/ic_fluent_pin_off_48_regular.svg",
              color: this.props.messagesStore.highlightColor,
            }, {
              action: "edit",
              iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Edit/SVG/ic_fluent_edit_48_regular.svg",
              color: this.props.messagesStore.highlightColor,
            }, {
              action: index === 0 ? undefined : "moveUp",
              iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg",
              color: index === 0 ? "grey" : this.props.messagesStore.highlightColor,
            }, {
              action: index === this.state.pinnedMessages.length - 1 ? undefined : "moveDown",
              iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg",
              color: index === this.state.pinnedMessages.length - 1 ? "grey" : this.props.messagesStore.highlightColor,
            }
            ]
          }
        );
      });
    }
    const expiredpPinnedMessagesList: IListElement[] = [];
    if (this.state.expiredPinnedMessages?.length > 0) {
      this.state.expiredPinnedMessages.forEach((message) => {
        expiredpPinnedMessagesList.push(
          {
            id: `${message.id}`,
            object: message,
            columns: [
              {
                header: "",
                type: "customElement",
                element: [
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    height: "100%",
                    opacity: 0.5
                  }}>
                    {message.title}
                  </div>
                ]
              }
            ],
            buttons: [{
              action: "unpin",
              iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Pin Off/SVG/ic_fluent_pin_off_48_regular.svg",
              color: this.props.messagesStore.highlightColor,
            }, {
              action: "edit",
              iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Edit/SVG/ic_fluent_edit_48_regular.svg",
              color: this.props.messagesStore.highlightColor,
            }
            ]
          }
        );
      });
    }

    const skeleton: ISkeletonElement[] = [
      {
        height: 20,
        width: 200,
        borderRadius: 5,
        marginTop: 15,
        marginLeft: 20
      },
      {
        height: 50,
        width: "auto",
        borderRadius: 5,
        marginTop: 20,
        marginLeft: 20,
        marginRight: 20
      },
      {
        height: 50,
        width: "auto",
        borderRadius: 5,
        marginTop: 1,
        marginLeft: 20,
        marginRight: 20
      },
      {
        height: 50,
        width: "auto",
        borderRadius: 5,
        marginTop: 1,
        marginLeft: 20,
        marginRight: 20
      }
    ];

    return (
      <IAPanel
        showNavigationBar={true}
        date={date}
        isMobile={this.props.messagesStore.isMobile}
        open={this.props.messagesStore.showSortPinnedPanel}
        transition={"slide"}
        width={this.props.messagesStore.isMobile ? window.innerWidth : (this.props.messagesStore.isFullPageView ? window.innerWidth : 700)}
        highlightColor={this.props.messagesStore.highlightColor}
        marginTop={this.props.top ? this.props.top : top}
        user={this.props.messagesStore.user}
        itemId={this.props.messagesStore.messageToOpen?.id?.toString()}
        tenant={this.props.messagesStore.tenant}
        component={this.props.messagesStore.component}
        instance={this.props.messagesStore.instance}
        token={this.props.messagesStore.token}
        environment={this.props.messagesStore.environment}
        source={this.props.messagesStore.source}
        mobileCloseButton={true}
        webpartId={this.props.messagesStore.webpartId}
        close={() => {
          if (this.props.messagesStore && this.props.messagesStore.showSortPinnedPanel) {
            this.props.messagesStore.showSortPinnedPanel = false;
            this.setState({ pinnedMessages: undefined });
          }
        }}
        loading={this.state.isLoading}
        skeletonLoaderElements={skeleton}
        panelId={this.panelId}
        enablePrint={false}
        navigationsBarContent={
          <>
            {!this.state.hasError &&
              <IAButton
                label={this.localizationService.strings.Messages_Categories_Save}
                disbaled={this.state.isLoading}
                buttonColor={this.props.messagesStore.highlightColor}
                onClick={() => {
                  this.setState({ isLoading: true });
                  const instance: IInstance = { ...this.props.messagesStore.instance };
                  instance.settings.pinSortOrder = this.state.pinnedMessages.map(message => message.id);
                  InstanceService.updateInstance(
                    this.props.messagesStore.environment,
                    this.props.messagesStore.token,
                    this.props.messagesStore.tenant,
                    "IntraActive-Messages",
                    instance)
                    .then(() => {
                      this.props.messagesStore.getInstance(true)
                        .then(() => {
                          this.props.getMessages();
                          this.props.messagesStore.showSortPinnedPanel = false;
                          this.setState({
                            isLoading: false,
                            pinnedMessages: undefined
                          });
                        });
                    })
                    .catch(() => this.setState({
                      hasError: true,
                      isLoading: false,
                      pinnedMessages: undefined
                    }))
                }}
                borderRadius={5}
                style={{
                  float: "right",
                  position: "relative",
                  marginTop: 7,
                  marginRight: 10
                }}
              />
            }
          </>
        }
      >
        <div
          style={{ padding: "0 20px 20px 20px" }}
        >
          <h2>{this.localizationService.strings.Messages_PinnedMessagesSortOrder}</h2>
          {this.state.hasError
            ?
            <>
              <p>{this.localizationService.strings.Messages_PinnedMessagesError}</p>
              <IAButton
                buttonColor={this.props.messagesStore.highlightColor}
                label={this.localizationService.strings.Messages_PinnedMessagesErrorTryAgain}
                onClick={() => {
                  this.getPinnedMessages();
                }}
              />
            </>
            :
            <>
              {pinnedMessagesList?.length > 0 ?
                <IAListView
                  id="pinnedMessages"
                  listElements={pinnedMessagesList}
                  backgroundColor={"#f6f6f5"}
                  hoverBackgroundColor={"#e6e6e6"}
                  buttonAreaDividerColor={"#dcdcdc"}
                  darkMode={false}
                  defaultAction={"edit"}
                  selectedAction={(action: string, id: string) => {
                    const index = this.state.pinnedMessages.findIndex(message => message.id === parseInt(id));
                    let newOrder = [...this.state.pinnedMessages];
                    switch (action) {
                      case "unpin":
                        this.props.messagesStore.messageToUnpin = this.state.pinnedMessages.find(message => message.id === parseInt(id));
                        this.props.messagesStore.showUnpinPopup = true;
                        break;
                      case "edit":
                        this.props.messagesStore.messageToEdit = this.state.pinnedMessages.find(message => message.id === parseInt(id));
                        this.setState({
                          isLoading: undefined,
                          pinnedMessages: undefined,
                          expiredPinnedMessages: undefined
                        });
                        break;
                      case "moveUp":
                        newOrder.splice(index - 1, 0, newOrder.splice(index, 1)[0]);
                        break;
                      case "moveDown":
                        newOrder.splice(index + 1, 0, newOrder.splice(index, 1)[0]);
                        break;
                      default:
                        break;
                    }
                    setTimeout(() => {
                      this.setState({ pinnedMessages: newOrder });
                    }, 10);
                  }}
                />
                :
                <div style={{
                  color: "gray"
                }}>{this.localizationService.strings.Messages_NoPinnedMessages}</div>
              }
              {expiredpPinnedMessagesList?.length > 0 &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  width: "100%"
                }}>
                  <h2>{this.localizationService.strings.Messages_PinnedMessagesExpiredMessages}</h2>
                  <IAListView
                    id="pinnedMessages"
                    listElements={expiredpPinnedMessagesList}
                    backgroundColor={"#f6f6f5"}
                    hoverBackgroundColor={"#e6e6e6"}
                    buttonAreaDividerColor={"#dcdcdc"}
                    darkMode={false}
                    defaultAction={"edit"}
                    selectedAction={(action: string, id: string) => {
                      switch (action) {
                        case "unpin":
                          this.props.messagesStore.messageToUnpin = this.state.expiredPinnedMessages.find(message => message.id === parseInt(id));
                          this.props.messagesStore.showUnpinPopup = true;
                          break;
                        case "edit":
                          this.props.messagesStore.messageToEdit = this.state.expiredPinnedMessages.find(message => message.id === parseInt(id));
                          this.setState({
                            isLoading: undefined,
                            pinnedMessages: undefined,
                            expiredPinnedMessages: undefined
                          });
                          break;
                        default:
                          break;
                      }
                    }}
                  />
                </div>
              }
            </>
          }
          {this.props.messagesStore.showUnpinPopup &&
            <IAPopup
              headline={this.localizationService.strings.Messages_UnpinPopupHeadline}
              content={
                <div>
                  <div
                    style={{
                      float: "left"
                    }}
                  >
                    {this.localizationService.strings.Messages_UnpinAreYouSure}
                  </div>
                  <div
                    style={{
                      clear: "both",
                      float: "left"
                    }}
                  >
                    <IAButton
                      label={this.localizationService.strings.Messages_Unpin}
                      buttonColor={this.props.messagesStore.highlightColor}
                      showSpinner={false}
                      onClick={() => {
                        this.setState({ isLoading: true }, () => {
                          this.props.messagesStore.messageToUnpin.isPinned = false;
                          this.props.messagesStore.messageToUnpin.pinnedEndDate = undefined;
                          this.props.messagesStore.showUnpinPopup = false;
                          MessageService.updateMessage(this.props.messagesStore.messageToUnpin, this.props.messagesStore.environment != undefined ? this.props.messagesStore.environment : "Production", this.props.messagesStore.token, this.props.messagesStore.tenant, this.props.messagesStore.instance.instanceName, undefined).then(async (httpStatus: IHttpStatus) => {
                            this.props.messagesStore.messageToUnpin = undefined;
                            this.getPinnedMessages();
                            console.log(httpStatus);
                          });
                        });
                      }}
                      style={{
                        marginTop: 20,
                        float: "left"
                      }}
                    />
                  </div>
                </div>
              }
              close={() => {
                this.props.messagesStore.messageToUnpin = undefined;
                this.props.messagesStore.showUnpinPopup = false;
              }}
            />
          }
        </div>
      </IAPanel >
    );
  }
}